import { FC, useMemo } from 'react';
import { Alert, Select, Skeleton } from 'antd';
import { HorizLayout } from 'packages/omnitag/text-search/search-top-panel';
import 'packages/omnitag/text-search/search-ui.css';
import { useGetRequestIDs } from 'packages/omnitag/api/use-get-request-ids';
import { requestIDAtom } from 'packages/omnitag/state/text-search';
import { useAtom } from 'jotai';
import { HelpTooltip } from 'packages/lib/common-ui/helpTooltip';
import useSWR from 'swr';
import { getTenantName } from 'packages/lib/auth';

/** The Request ID is used to track active Omnitag time by Request ID (metrics)
 * For contractors only, selecting it is required before a search can be performed.
 */
export const RequestIDDropdown: FC = (): JSX.Element => {
  const requestIDsSWR = useGetRequestIDs();
  const [requestID, setRequestID] = useAtom(requestIDAtom);
  const { data: tenant } = useSWR('tenant-name', getTenantName);
  const isContractorToken = tenant === 'contractor';

  const handleChange = (value: string | undefined) => {
    setRequestID(value ?? null);
  };

  const options = useMemo(() => {
    return requestIDsSWR.data
      ?.map((requestID) => {
        return { label: requestID, value: requestID };
      })
      .sort();
  }, [requestIDsSWR.data]);

  return (
    <HorizLayout style={{ minWidth: '200px' }}>
      {requestIDsSWR.error ? (
        <Alert message="Failed to load Request IDs" type="error" />
      ) : (
        <>
          {requestIDsSWR.isLoading ? (
            <Skeleton active title paragraph={false} />
          ) : (
            <>
              {!isContractorToken && (
                <HelpTooltip
                  body={
                    <>
                      Request ID is used by members of the Data Annotations
                      team, to automatically track the amount of time spent
                      fulfilling a request.
                      <br />
                      <br />
                      Other users should leave this field blank.
                    </>
                  }
                />
              )}
              <Select
                allowClear
                placeholder="Select a Request ID"
                style={{ minWidth: 180 }}
                listHeight={500}
                onChange={handleChange}
                options={options}
                value={requestID}
              />
            </>
          )}
        </>
      )}
    </HorizLayout>
  );
};
